import { useState, useEffect, useCallback, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { 
  MapPin, 
  Filter,
  Building,
  CheckCircle2,
  Clock,
  ChevronRight,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import franceGeoJson from "@/data/france_departments.geojson";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

// Mapping code département -> nom
const DEPT_NAMES = {
  "01": "Ain", "02": "Aisne", "03": "Allier", "04": "Alpes-de-Haute-Provence", "05": "Hautes-Alpes",
  "06": "Alpes-Maritimes", "07": "Ardèche", "08": "Ardennes", "09": "Ariège", "10": "Aube",
  "11": "Aude", "12": "Aveyron", "13": "Bouches-du-Rhône", "14": "Calvados", "15": "Cantal",
  "16": "Charente", "17": "Charente-Maritime", "18": "Cher", "19": "Corrèze", "21": "Côte-d'Or",
  "22": "Côtes-d'Armor", "23": "Creuse", "24": "Dordogne", "25": "Doubs", "26": "Drôme",
  "27": "Eure", "28": "Eure-et-Loir", "29": "Finistère", "2A": "Corse-du-Sud", "2B": "Haute-Corse",
  "30": "Gard", "31": "Haute-Garonne", "32": "Gers", "33": "Gironde", "34": "Hérault",
  "35": "Ille-et-Vilaine", "36": "Indre", "37": "Indre-et-Loire", "38": "Isère", "39": "Jura",
  "40": "Landes", "41": "Loir-et-Cher", "42": "Loire", "43": "Haute-Loire", "44": "Loire-Atlantique",
  "45": "Loiret", "46": "Lot", "47": "Lot-et-Garonne", "48": "Lozère", "49": "Maine-et-Loire",
  "50": "Manche", "51": "Marne", "52": "Haute-Marne", "53": "Mayenne", "54": "Meurthe-et-Moselle",
  "55": "Meuse", "56": "Morbihan", "57": "Moselle", "58": "Nièvre", "59": "Nord",
  "60": "Oise", "61": "Orne", "62": "Pas-de-Calais", "63": "Puy-de-Dôme", "64": "Pyrénées-Atlantiques",
  "65": "Hautes-Pyrénées", "66": "Pyrénées-Orientales", "67": "Bas-Rhin", "68": "Haut-Rhin", "69": "Rhône",
  "70": "Haute-Saône", "71": "Saône-et-Loire", "72": "Sarthe", "73": "Savoie", "74": "Haute-Savoie",
  "75": "Paris", "76": "Seine-Maritime", "77": "Seine-et-Marne", "78": "Yvelines", "79": "Deux-Sèvres",
  "80": "Somme", "81": "Tarn", "82": "Tarn-et-Garonne", "83": "Var", "84": "Vaucluse",
  "85": "Vendée", "86": "Vienne", "87": "Haute-Vienne", "88": "Vosges", "89": "Yonne",
  "90": "Territoire de Belfort", "91": "Essonne", "92": "Hauts-de-Seine", "93": "Seine-Saint-Denis",
  "94": "Val-de-Marne", "95": "Val-d'Oise"
};

export default function MapFrance() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [departementStats, setDepartementStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptChantiers, setDeptChantiers] = useState([]);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [hoveredDept, setHoveredDept] = useState(null);
  
  // User context
  const userRole = localStorage.getItem("label_energie_role") || "";
  const currentUser = localStorage.getItem("label_energie_user") || "";
  const isRegieTelepro = userRole === "regie" || userRole === "telepro";
  
  // Filtres
  const [statutFilter, setStatutFilter] = useState(searchParams.get("statut") || "all");
  const [programmeFilter, setProgrammeFilter] = useState(searchParams.get("programme") || "all");
  
  // Charger les stats par département
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDepartementStats();
  }, [statutFilter, programmeFilter]);
  
  const fetchDepartementStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statutFilter !== "all") params.append("statut", statutFilter);
      if (programmeFilter !== "all") params.append("programme", programmeFilter);
      if (isRegieTelepro) params.append("user", currentUser);
      
      const response = await axios.get(`${API}/carte/departements?${params.toString()}`);
      setDepartementStats(response.data);
    } catch (error) {
      console.error("Erreur chargement carte:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Charger les chantiers d'un département
  const fetchDeptChantiers = async (dept) => {
    try {
      const params = new URLSearchParams();
      if (statutFilter !== "all") params.append("statut", statutFilter);
      if (programmeFilter !== "all") params.append("programme", programmeFilter);
      if (isRegieTelepro) params.append("user", currentUser);
      
      const response = await axios.get(`${API}/carte/chantiers/${dept}?${params.toString()}`);
      setDeptChantiers(response.data);
    } catch (error) {
      console.error("Erreur chargement chantiers:", error);
      setDeptChantiers([]);
    }
  };
  
  // Clic sur un département
  const handleDeptClick = useCallback(async (deptCode) => {
    const deptStat = departementStats.find(d => d.departement === deptCode);
    if (deptStat) {
      if (deptStat.chantiers.length === 1) {
        const dossierId = deptStat.chantiers[0].id;
        navigate(isRegieTelepro ? `/dossier-regie/${dossierId}` : `/dossiers/${dossierId}`);
      } else {
        setSelectedDept(deptCode);
        await fetchDeptChantiers(deptCode);
        setShowDeptModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departementStats, navigate, statutFilter, programmeFilter, isRegieTelepro]);
  
  // Obtenir le code département depuis les propriétés GeoJSON
  const getDeptCode = (properties) => {
    return properties.code;
  };
  
  // Obtenir les stats d'un département
  const getDeptStats = (deptCode) => {
    return departementStats.find(d => d.departement === deptCode);
  };
  
  // Couleur du département
  const getDeptColor = (deptCode) => {
    const stats = getDeptStats(deptCode);
    if (!stats) return "#e5e7eb";
    
    const hasSav = stats.sav > 0;
    const hasEnCours = stats.en_cours > 0;
    const hasRealise = stats.realise > 0;
    
    if (hasSav) return "#ef4444"; // Red - SAV
    if (hasEnCours && hasRealise) return "#84cc16"; // Lime - mixte
    if (hasEnCours) return "#f59e0b"; // Orange - en cours
    return "#22c55e"; // Vert - réalisé
  };
  
  // Mise à jour des filtres
  const handleStatutChange = (value) => {
    setStatutFilter(value);
    const params = new URLSearchParams(searchParams);
    if (value === "all") params.delete("statut");
    else params.set("statut", value);
    setSearchParams(params);
  };
  
  const handleProgrammeChange = (value) => {
    setProgrammeFilter(value);
    const params = new URLSearchParams(searchParams);
    if (value === "all") params.delete("programme");
    else params.set("programme", value);
    setSearchParams(params);
  };
  
  // Calculer les totaux
  const totals = departementStats.reduce((acc, dept) => {
    acc.total += dept.total;
    acc.en_cours += dept.en_cours;
    acc.realise += dept.realise;
    acc.sav += (dept.sav || 0);
    acc.mpr += dept.mpr;
    acc["174"] += dept["174"];
    acc.cumule += (dept.cumule || 0);
    return acc;
  }, { total: 0, en_cours: 0, realise: 0, sav: 0, mpr: 0, "174": 0, cumule: 0 });

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
            Carte des chantiers
          </h1>
          <p className="text-muted-foreground mt-1">
            {totals.total} chantier{totals.total > 1 ? "s" : ""} sur la carte
          </p>
        </div>
        
        {/* Filtres */}
        <div className="flex gap-3 flex-wrap">
          <Select value={statutFilter} onValueChange={handleStatutChange}>
            <SelectTrigger className="w-[160px] rounded-xl bg-card" data-testid="statut-filter">
              <Clock className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="en_cours">En cours</SelectItem>
              <SelectItem value="realise">Réalisé</SelectItem>
              <SelectItem value="sav">SAV</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={programmeFilter} onValueChange={handleProgrammeChange}>
            <SelectTrigger className="w-[140px] rounded-xl bg-card" data-testid="programme-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Programme" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="mpr">MPR</SelectItem>
              <SelectItem value="174">174</SelectItem>
              <SelectItem value="cumule">Op. Cumulée</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Stats résumé — clickable boxes */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className={cn("bg-card border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md", statutFilter === "all" && "ring-2 ring-border")} onClick={() => handleStatutChange("all")}>
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <MapPin className="w-4 h-4" />
            Total chantiers
          </div>
          <p className="text-2xl font-bold">{totals.total}</p>
          <div className="flex gap-2 text-xs text-muted-foreground mt-1">
            <span>MPR: {totals.mpr}</span>
            <span>174: {totals["174"]}</span>
            {totals.cumule > 0 && <span className="text-violet-600">Cum.: {totals.cumule}</span>}
          </div>
        </div>
        <div className={cn("bg-card border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md", statutFilter === "en_cours" && "ring-2 ring-amber-500")} onClick={() => handleStatutChange(statutFilter === "en_cours" ? "all" : "en_cours")}>
          <div className="flex items-center gap-2 text-amber-600 text-sm mb-1">
            <Clock className="w-4 h-4" />
            En cours
          </div>
          <p className="text-2xl font-bold text-amber-600">{totals.en_cours}</p>
        </div>
        <div className={cn("bg-card border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md", statutFilter === "realise" && "ring-2 ring-emerald-500")} onClick={() => handleStatutChange(statutFilter === "realise" ? "all" : "realise")}>
          <div className="flex items-center gap-2 text-emerald-600 text-sm mb-1">
            <CheckCircle2 className="w-4 h-4" />
            Réalisés
          </div>
          <p className="text-2xl font-bold text-emerald-600">{totals.realise}</p>
        </div>
        <div className={cn("bg-card border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md", statutFilter === "sav" && "ring-2 ring-red-500")} onClick={() => handleStatutChange(statutFilter === "sav" ? "all" : "sav")}>
          <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
            <AlertTriangle className="w-4 h-4" />
            SAV
          </div>
          <p className="text-2xl font-bold text-red-600">{totals.sav}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <Building className="w-4 h-4" />
            Départements
          </div>
          <p className="text-2xl font-bold">{departementStats.length}</p>
        </div>
      </div>

      {/* Légende */}
      <div className="flex items-center gap-6 flex-wrap text-xs">
        <span className="font-semibold text-muted-foreground">Légende :</span>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#f59e0b" }} /><span>En cours</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#22c55e" }} /><span>Réalisé</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#84cc16" }} /><span>Mixte</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#ef4444" }} /><span>SAV</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#e5e7eb" }} /><span>Aucun chantier</span></div>
      </div>
      
      {/* Carte de France */}
      <div className="bg-card border border-border rounded-xl p-4 md:p-6 relative overflow-hidden">
        {loading ? (
          <div className="h-[500px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Chargement de la carte...</div>
          </div>
        ) : (
          <div className="relative">
            {/* Tooltip */}
            {hoveredDept && (
              <div className="absolute top-4 right-4 bg-card border border-border rounded-lg p-3 shadow-lg z-10">
                <p className="font-semibold">{hoveredDept.code} - {DEPT_NAMES[hoveredDept.code] || hoveredDept.nom}</p>
                {getDeptStats(hoveredDept.code) ? (
                  <p className="text-sm text-muted-foreground">
                    {getDeptStats(hoveredDept.code).total} chantier(s)
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun chantier</p>
                )}
              </div>
            )}
            
            {/* Carte */}
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{
                center: [2.5, 46.8],
                scale: 2600
              }}
              style={{ width: "100%", height: "auto", maxHeight: "550px" }}
            >
              <Geographies geography={franceGeoJson}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const deptCode = getDeptCode(geo.properties);
                    const deptStats = getDeptStats(deptCode);
                    const hasChantiers = !!deptStats;
                    
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={getDeptColor(deptCode)}
                        stroke="#fff"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: "none" },
                          hover: { 
                            fill: hasChantiers ? "#16a34a" : "#d1d5db",
                            outline: "none",
                            cursor: hasChantiers ? "pointer" : "default"
                          },
                          pressed: { outline: "none" }
                        }}
                        onMouseEnter={() => {
                          setHoveredDept({ code: deptCode, nom: geo.properties.nom });
                        }}
                        onMouseLeave={() => {
                          setHoveredDept(null);
                        }}
                        onClick={() => {
                          if (hasChantiers) {
                            handleDeptClick(deptCode);
                          }
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>
            
            {/* Légende */}
            <div className="absolute bottom-4 left-4 bg-card/95 backdrop-blur border border-border rounded-xl p-4 shadow-lg">
              <p className="text-xs font-semibold text-muted-foreground mb-3">Légende</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded bg-[#e5e7eb] border border-gray-300"></div>
                  <span>Sans chantier</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded bg-amber-500"></div>
                  <span>En cours</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded bg-emerald-500"></div>
                  <span>Réalisés</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded bg-lime-500"></div>
                  <span>Mixte</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Liste des départements avec chantiers */}
      {departementStats.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="font-heading font-semibold">Départements avec chantiers</h2>
          </div>
          <div className="divide-y divide-border max-h-[300px] overflow-y-auto">
            {departementStats
              .sort((a, b) => b.total - a.total)
              .map((deptStat) => (
                <div
                  key={deptStat.departement}
                  className="p-4 flex items-center justify-between hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleDeptClick(deptStat.departement)}
                  data-testid={`dept-row-${deptStat.departement}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <span className="font-bold text-primary text-sm">{deptStat.departement}</span>
                    </div>
                    <div>
                      <p className="font-medium">{DEPT_NAMES[deptStat.departement] || `Département ${deptStat.departement}`}</p>
                      <p className="text-sm text-muted-foreground">{deptStat.total} chantier{deptStat.total > 1 ? "s" : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="hidden sm:flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">MPR:</span>
                      <span className="font-medium">{deptStat.mpr}</span>
                    </div>
                    <div className="hidden sm:flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">174:</span>
                      <span className="font-medium">{deptStat["174"]}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {deptStat.en_cours > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-medium">
                          <Clock className="w-3 h-3" />
                          {deptStat.en_cours}
                        </span>
                      )}
                      {deptStat.realise > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                          <CheckCircle2 className="w-3 h-3" />
                          {deptStat.realise}
                        </span>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
      
      {/* Modal détail département */}
      <Dialog open={showDeptModal} onOpenChange={setShowDeptModal}>
        <DialogContent className="max-w-lg rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <span className="font-bold text-primary">{selectedDept}</span>
              </div>
              <div>
                <p>{DEPT_NAMES[selectedDept] || `Département ${selectedDept}`}</p>
                <p className="text-sm font-normal text-muted-foreground">
                  {deptChantiers.length} chantier{deptChantiers.length > 1 ? "s" : ""}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Stats du département */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold">{deptChantiers.length}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div className="bg-amber-500/10 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">
                  {deptChantiers.filter(c => c.statut === "en_cours").length}
                </p>
                <p className="text-xs text-amber-600">En cours</p>
              </div>
              <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">
                  {deptChantiers.filter(c => c.statut === "realise").length}
                </p>
                <p className="text-xs text-emerald-600">Réalisés</p>
              </div>
              <div className="bg-red-500/10 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-600">
                  {deptChantiers.filter(c => c.statut === "sav").length}
                </p>
                <p className="text-xs text-red-600">SAV</p>
              </div>
            </div>
            
            {/* Liste des chantiers */}
            <div className="border border-border rounded-lg divide-y divide-border max-h-[300px] overflow-y-auto">
              {deptChantiers.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  Aucun chantier dans ce département
                </div>
              ) : (
                deptChantiers.map((chantier) => (
                  <div
                    key={chantier.id}
                    className="p-3 flex items-center justify-between hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => {
                      navigate(isRegieTelepro ? `/dossier-regie/${chantier.id}` : `/dossiers/${chantier.id}`);
                      setShowDeptModal(false);
                    }}
                    data-testid={`chantier-${chantier.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        chantier.statut === "sav" ? "bg-red-500" : chantier.statut === "en_cours" ? "bg-amber-500" : "bg-emerald-500"
                      )}></div>
                      <div>
                        <p className="font-medium text-sm">{chantier.client_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {chantier.programme} - {chantier.code_postal}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "text-xs px-2 py-1 rounded-full",
                        chantier.statut === "en_cours" 
                          ? "bg-amber-500/20 text-amber-600 dark:text-amber-400" 
                          : "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                      )}>
                        {chantier.statut === "en_cours" ? "En cours" : "Réalisé"}
                      </span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {deptChantiers.length > 0 && (
              <Button 
                onClick={() => {
                  navigate(`/dossiers`);
                  setShowDeptModal(false);
                }}
                className="w-full rounded-xl bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)]"
              >
                Voir tous les dossiers
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
